/**
 * Client-side cache for template matching results.
 * 
 * Optimization: Since matches for a lower threshold are strictly a subset
 * of matches for a higher threshold (lower threshold = stricter filtering),
 * we can filter cached results client-side when the user requests a lower
 * threshold than what's cached.
 *
 * The cache is also mode-aware: tagged and untagged produce fundamentally
 * different result sets, so each mode is cached separately.
 */

import { MatchingMode, TemplateMatchResult } from '../types';

interface CacheEntry {
  threshold: number;           // Threshold used for the API call
  matches: TemplateMatchResult[];
  timestamp: number;           // Date.now() when cached
}

// Key format: `${docId}:${templateId}:${mode}`
const cache = new Map<string, CacheEntry>();

// Cache TTL: 3 hours in milliseconds
const TTL_MS = 3 * 60 * 60 * 1000;

/**
 * Generate cache key from docId, templateId, and mode.
 */
function getCacheKey(docId: string, templateId: string, mode: MatchingMode): string {
  return `${docId}:${templateId}:${mode}`;
}

/**
 * Check if a cache entry is expired.
 */
function isExpired(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp > TTL_MS;
}

/**
 * Get cached result if valid (not expired).
 * Returns null if no cache exists or if expired.
 */
export function getCached(docId: string, templateId: string, mode: MatchingMode): CacheEntry | null {
  const key = getCacheKey(docId, templateId, mode);
  const entry = cache.get(key);
  
  if (!entry) {
    return null;
  }
  
  if (isExpired(entry)) {
    cache.delete(key);
    return null;
  }
  
  return entry;
}

/**
 * Store result in cache with current timestamp.
 */
export function setCache(
  docId: string,
  templateId: string,
  mode: MatchingMode,
  threshold: number,
  matches: TemplateMatchResult[]
): void {
  const key = getCacheKey(docId, templateId, mode);
  cache.set(key, {
    threshold,
    matches,
    timestamp: Date.now(),
  });
}

/**
 * Remove all expired entries from the cache.
 * Called lazily to avoid needing a background timer.
 */
export function clearExpired(): void {
  const now = Date.now();
  for (const [key, entry] of cache.entries()) {
    if (now - entry.timestamp > TTL_MS) {
      cache.delete(key);
    }
  }
}

/**
 * Invalidate all cache entries for a specific document.
 * Useful when the document changes or is reloaded.
 */
export function invalidateForDoc(docId: string): void {
  const prefix = `${docId}:`;
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

/**
 * Invalidate cache for a specific document and template combination (all modes).
 */
export function invalidateEntry(docId: string, templateId: string): void {
  const prefix = `${docId}:${templateId}:`;
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

/**
 * Clear the entire cache.
 */
export function clearAll(): void {
  cache.clear();
}
